import {
    Box,
    Button,
    Grid,
    GridItem,
    Heading,
    Input,
    InputGroup,
    InputRightElement,
    Link,
    Text,
    useToast
} from '@chakra-ui/react'
import { yupResolver } from '@hookform/resolvers/yup'
import { Icon, Layout } from 'components/atoms'
import { InputBuild, ModalBuild, SelectBuild } from 'components/molecules'
import { ModalBuildHandle } from 'components/molecules/ModalBuild/interfaces'
import { TableBuild } from 'components/organisms'
import { TableBuildProps } from 'components/organisms/TableBuild/interfaces'
import { TableBuildActionsType } from 'components/organisms/TableBuild/type'
import { useAuthContext } from 'contexts/auth'
import { useInstitutionsContext } from 'contexts/institutions'
import {
    AgentOrderBy,
    AgentResponseDTO,
    InstitutionsLinkAgentOrderBy,
    InstitutionsLinkAgentQueryDTO
} from 'models'
import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link as LinkRouter, useParams } from 'react-router-dom'
import { consultAgent } from 'services/agent'
import formatter from 'utils/formatter'
import { useDebounce } from 'utils/useDebounce'
import useStep from 'utils/useStep'
import * as yup from 'yup'
import { InputFormAgent } from './form'
import { FormAgentElementsValues } from './interface'
import { IconReturn } from './styles'
import { FieldFormAgentElements } from './types'

const Agent: React.FC = () => {
    const { isBackoffice } = useAuthContext()

    const {
        getLinkInstitutionAgent,
        createLinkInstitutionAgent,
        deleteLinkInstitutionAgent
    } = useInstitutionsContext()

    const [currentStep, helpers] = useStep(3)

    const { goToNextStep, goToPrevStep, resetStep, setStep } = helpers

    const toast = useToast()

    const { id = '' } = useParams()

    const [searching, setSearching] = useState(false)
    const [agents, setAgents] = useState<AgentResponseDTO[]>()
    const [agentSeleted, setAgentSelected] = useState('')
    const [agentsLink, setAgentsLink] = useState<AgentResponseDTO[]>()
    const [selectAgentLink, setSelectAgentLink] = useState<string[]>([''])

    const [loading, setLoading] = useState(true)
    const [filter, setFilter] = useState('')
    const queryFilter: InstitutionsLinkAgentQueryDTO = filter
        ? {
              razaoSocial: filter,
              cnpj: filter.replace(/\D/g, ''),
              codigo: filter,
              uf: filter
          }
        : {}

    const [page, setPage] = useState<number>(1)
    const [limit, setLimit] = useState(10)
    const [totalPages, setTotalPages] = useState(1)
    const [dataAgenteBoolean, setDataAgenteBoolean] = useState<boolean>(false)
    const [orderBy, setOrderBy] = useState<
        Required<
            Pick<InstitutionsLinkAgentQueryDTO, 'ordenacao' | 'tipoOrdenacao'>
        >
    >({
        ordenacao: 'razaoSocial',
        tipoOrdenacao: 'ASC'
    })

    const addModalRef = useRef<ModalBuildHandle>(null)
    const deleteModalRef = useRef<ModalBuildHandle>(null)

    const handleDeleteModalOpen = (id: string): void => {
        setAgentSelected(id)
        deleteModalRef.current?.onOpen()
    }

    const handleDeleteModalClose = (): void => {
        deleteModalRef.current?.onClose()
    }

    const handleCreateModalOpen = (): void => {
        addModalRef.current?.onOpen()
    }

    const handleCreateModalClose = (): void => {
        addModalRef.current?.onClose()
    }

    const [table, setTable] = useState<
        TableBuildProps<'uf' | 'codigo' | 'cnpj' | 'razaoSocial'>
    >({
        header: [
            {
                name: 'uf',
                text: 'UF',
                order: true,
                position: 0
            },
            {
                name: 'codigo',
                text: 'Código',
                order: true,
                position: 1
            },
            {
                name: 'razaoSocial',
                text: 'Razão Social',
                order: true,
                position: 2
            },
            {
                name: 'cnpj',
                text: 'CNPJ',
                order: true,
                position: 3
            }
        ],
        action: [
            {
                type: 'DELETE'
            }
        ]
    })

    const handleAction = (type: TableBuildActionsType, id: number | string) => {
        switch (type) {
            case 'DELETE':
                handleDeleteModalOpen(String(id))
                break
        }
    }

    const handlePage = (value: number) => {
        setPage(value)
        setLoading(true)
    }

    const handleLimit = (value: number) => {
        setLimit(value)
        setLoading(true)
    }

    const handleSplitSelect = (data: string) => {
        setSelectAgentLink(data.split('|'))
    }

    const handleOrderBy = (ordenacao: InstitutionsLinkAgentOrderBy) => {
        setOrderBy(old => ({
            ordenacao,
            tipoOrdenacao:
                old.ordenacao !== ordenacao
                    ? old.tipoOrdenacao
                    : old.tipoOrdenacao === 'ASC'
                    ? 'DESC'
                    : 'ASC'
        }))
        setLoading(true)
    }

    const schema = yup
        .object({
            codigo: yup.string().required(),
            uf: yup.string().required(),
            agenteFinanceiroVinculo: yup.string().required()
        })
        .required()

    const {
        handleSubmit,
        register,
        watch,
        reset,
        trigger,
        clearErrors,
        formState: { errors, isSubmitting }
    } = useForm<FormAgentElementsValues>({
        resolver: yupResolver(schema)
    })

    const btnCtaText: {
        [index: string]: {
            type: 'reset' | 'button' | 'submit' | undefined
            text: string
        }
    } = {
        1: { type: 'button', text: 'Buscar' },
        2: { type: 'button', text: 'Selecionar' },
        3: { type: 'submit', text: 'Incluir agente' }
    }

    const handleConsultAgent = ({ codigo, uf }: any) => {
        clearErrors()
        setSearching(true)

        codigo && uf
            ? consultAgent(codigo, uf)
                  .then(({ data }) => {
                      setAgents(data)
                      data.length === 0
                          ? setDataAgenteBoolean(true)
                          : setDataAgenteBoolean(false)
                      data.length && goToNextStep()
                  })
                  .catch(() => {
                      toast({
                          title: 'Sentimos muito!',
                          description:
                              'Ocorreu um erro, tente novamente mais tarde.',
                          position: 'bottom-left',
                          status: 'error',
                          isClosable: true
                      })
                  })
                  .finally(() => setSearching(false))
            : trigger().finally(() => setSearching(false))
    }

    const handleDeleteAgent = () => {
        deleteLinkInstitutionAgent(id, agentSeleted)
            .then(() => {
                handleDeleteModalClose()
                toast({
                    title: 'Vinculo deletado com sucesso!',
                    position: 'bottom-left',
                    status: 'info',
                    isClosable: true
                })

                if (agentsLink?.length === 1 && page > 1) {
                    setPage(old => old - 1 || 1)
                    setTotalPages(old => old - 1 || 1)
                } else {
                    handleFilterAgent()
                }
            })
            .catch(() => {
                toast({
                    title: 'Sentimos muito!',
                    description: 'Ocorreu um erro, tente novamente mais tarde.',
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true
                })
            })
    }

    const handleFilterAgent = () => {
        setLoading(true)
        getLinkInstitutionAgent(id, {
            ...queryFilter,
            ...orderBy,
            pagina: page,
            limite: limit
        })
            .then(({ headers, data }) => {
                setAgentsLink(data)
                setTable(old => ({
                    ...old,
                    body: data.map(af => {
                        af.cnpj = formatter.cnpj(af.cnpj)
                        return af
                    })
                }))
                setTotalPages(+headers['total-pages'] || 1)
            })
            .catch(() => {
                toast({
                    title: 'Sentimos muito!',
                    description:
                        'Ocorreu algum erro, tente novamente mais tarde.',
                    position: 'bottom-left',
                    status: 'error',
                    isClosable: true
                })
            })
            .finally(() => setLoading(false))
    }

    const handleSearch = useDebounce((e: ChangeEvent<HTMLInputElement>) => {
        setLoading(true)
        const value = e.target.value.toLowerCase()
        setFilter(value)
        setPage(1)
    })

    const onSubmit = () => {
        agents &&
            selectAgentLink &&
            createLinkInstitutionAgent(id, {
                idAgenteFinanceiroDETRAN: selectAgentLink[2]
            })
                .then(() => {
                    toast({
                        title: 'Agente vinculado com sucesso!',
                        position: 'bottom-left',
                        status: 'info',
                        isClosable: true
                    })
                    setAgents(undefined)
                    setSelectAgentLink([])
                    handleFilterAgent()
                    onCancel()
                    resetStep()
                })
                .catch(() => {
                    toast({
                        title: 'Sentimos muito!',
                        description:
                            'Ocorreu algum erro, tente novamente mais tarde.',
                        position: 'bottom-left',
                        status: 'error',
                        isClosable: true
                    })
                })
    }

    const onCancel = () => {
        setDataAgenteBoolean(false)
        reset()
        resetStep()
        setAgents(undefined)
        setSelectAgentLink([])
        handleCreateModalClose()
    }

    useEffect(() => {
        handleFilterAgent()
    }, [filter, page, limit, orderBy])

    return (
        <>
            <Grid
                data-testid="agent-header"
                templateColumns={{ base: '1fr', sm: 'repeat(2, auto)' }}
                textAlign="end"
                alignItems="center"
                justifyContent="space-between"
                gap={{ base: 4, md: 6 }}
                px={6}
                py={4}
                mb={4}
            >
                <GridItem w="100%">
                    <InputGroup w="100%" data-testid="agent-input">
                        <Input
                            data-testid="agent-input-search"
                            minW={{ md: '400px' }}
                            id="search"
                            variant="flushed"
                            placeholder="Buscar por UF, código, nome ou CNPJ"
                            onChange={handleSearch}
                        />
                        <InputRightElement
                            data-testid="agent-input-icon"
                            as="label"
                            htmlFor="search"
                            children={<Icon name="search" />}
                        />
                    </InputGroup>
                </GridItem>
                {isBackoffice && (
                    <GridItem w="100%">
                        <Button
                            data-testid="agent-add-modal"
                            colorScheme="primary"
                            onClick={handleCreateModalOpen}
                        >
                            Vincular agente financeiro
                        </Button>
                    </GridItem>
                )}
            </Grid>
            <TableBuild
                header={table.header}
                body={table.body}
                ordenation={orderBy}
                action={table.action}
                error={{
                    action: {
                        text: filter ? 'search' : 'agentLink',
                        onClick: () => !filter && handleCreateModalOpen()
                    }
                }}
                loading={{
                    status: loading,
                    loader: [
                        {
                            height: '20px'
                        },
                        {
                            height: '20px'
                        },
                        {
                            height: '20px'
                        },
                        {
                            height: '20px'
                        },
                        {
                            height: '20px'
                        }
                    ]
                }}
                pagination={{
                    page,
                    limit,
                    totalPages
                }}
                onOrderBy={order =>
                    handleOrderBy(order as unknown as AgentOrderBy)
                }
                onAction={handleAction}
                onChangePage={handlePage}
                onChangeLimit={handleLimit}
            />

            <ModalBuild
                ref={deleteModalRef}
                closeOnOverlayClick={false}
                title="Confirmação de exclusão"
                size="md"
                actions={{
                    position: 'flex-end',
                    childrens: [
                        {
                            text: 'Cancelar',
                            handle: 'cancel',
                            variant: 'outline'
                        },
                        {
                            text: 'Deletar',
                            handle: 'confirm',
                            colorScheme: 'red'
                        }
                    ]
                }}
                onConfirm={handleDeleteAgent}
                onCancel={handleDeleteModalClose}
            >
                <Text px={6} py={4}>
                    Você realmente deseja deletar? Uma vez deletado, não será
                    possível voltar atrás.
                </Text>
            </ModalBuild>

            <ModalBuild
                ref={addModalRef}
                closeOnOverlayClick={false}
                title=" "
                size="xl"
                onAfterClose={onCancel}
                onCloseComplete={onCancel}
            >
                <form onSubmit={handleSubmit(onSubmit)}>
                    {agents?.length ? (
                        <IconReturn
                            name="linear"
                            onClick={() => {
                                setAgents(undefined)
                            }}
                        />
                    ) : (
                        ''
                    )}
                    <Layout type="container" gutter={{ y: 1.5 }} full px={6}>
                        <Layout type="row">
                            <Layout type="col">
                                <Heading size="md" textAlign={{ sm: 'center' }}>
                                    Agente financeiro
                                </Heading>
                            </Layout>
                        </Layout>
                        <Layout type="row">
                            <Layout type="col">
                                {currentStep === 1 ? (
                                    <Layout type="row">
                                        {InputFormAgent.map(
                                            ({ layout, input, select }) => (
                                                <Layout {...layout}>
                                                    {input ? (
                                                        <InputBuild
                                                            {...input}
                                                            err={
                                                                errors[
                                                                    `${
                                                                        input.id as FieldFormAgentElements
                                                                    }`
                                                                ]
                                                            }
                                                            {...register(
                                                                `${
                                                                    input.id as FieldFormAgentElements
                                                                }`
                                                            )}
                                                            value={watch(
                                                                `${
                                                                    input.id as FieldFormAgentElements
                                                                }`
                                                            )}
                                                        />
                                                    ) : (
                                                        select && (
                                                            <SelectBuild
                                                                {...select}
                                                                err={
                                                                    errors[
                                                                        select.id as FieldFormAgentElements
                                                                    ]
                                                                }
                                                                {...register(
                                                                    select.id as FieldFormAgentElements
                                                                )}
                                                            />
                                                        )
                                                    )}
                                                </Layout>
                                            )
                                        )}
                                        {dataAgenteBoolean && (
                                            <Text
                                                color="red.500"
                                                textAlign="center"
                                            >
                                                Agente Financeiro não
                                                encontrado,{' '}
                                                <Link
                                                    as={LinkRouter}
                                                    to="/agentes-financeiros"
                                                    textDecoration="underline"
                                                    fontWeight={600}
                                                >
                                                    cadastre agora
                                                </Link>
                                                .
                                            </Text>
                                        )}
                                    </Layout>
                                ) : agents?.length && currentStep === 2 ? (
                                    <Box display="flex" flexWrap="wrap" gap={4}>
                                        <Box w="auto">
                                            <Text as="span" fontWeight={500}>
                                                Código do Detran:{' '}
                                            </Text>
                                            {agents[0]?.codigo}
                                        </Box>
                                        <Box w="auto">
                                            <Text as="span" fontWeight={500}>
                                                UF:{' '}
                                            </Text>
                                            {agents[0]?.uf}{' '}
                                        </Box>
                                        <Box w="full">
                                            <SelectBuild
                                                id="agenteFinanceiroVinculo"
                                                required
                                                {...register(
                                                    'agenteFinanceiroVinculo'
                                                )}
                                                label="Agente financeiro"
                                                value={
                                                    selectAgentLink &&
                                                    selectAgentLink.join('|')
                                                }
                                                onChange={data =>
                                                    handleSplitSelect(
                                                        data.target.value
                                                    )
                                                }
                                                err={
                                                    errors[
                                                        'agenteFinanceiroVinculo'
                                                    ]
                                                }
                                                options={agents?.map(
                                                    (
                                                        {
                                                            cnpj,
                                                            razaoSocial,
                                                            id
                                                        },
                                                        index
                                                    ) => ({
                                                        text: `${cnpj} - ${razaoSocial}`,
                                                        value: `${index}|${cnpj}|${id}`
                                                    })
                                                )}
                                                fontWeight={500}
                                            />
                                        </Box>
                                    </Box>
                                ) : agents?.length &&
                                  currentStep === 3 &&
                                  selectAgentLink ? (
                                    <Box display="flex" flexWrap="wrap" gap={4}>
                                        <Box w="full">
                                            <Text as="span" fontWeight={500}>
                                                Razão social:{' '}
                                            </Text>
                                            {
                                                agents[
                                                    Number(selectAgentLink[0])
                                                ]?.razaoSocial
                                            }
                                        </Box>
                                        <Box w="auto">
                                            <Text as="span" fontWeight={500}>
                                                CNPJ:{' '}
                                            </Text>
                                            {agents &&
                                                formatter.cnpj(
                                                    agents[
                                                        Number(
                                                            selectAgentLink[0]
                                                        )
                                                    ]?.cnpj
                                                )}{' '}
                                        </Box>
                                        <Box w="auto">
                                            <Text as="span" fontWeight={500}>
                                                UF:{' '}
                                            </Text>
                                            {
                                                agents[
                                                    Number(selectAgentLink[0])
                                                ]?.uf
                                            }
                                        </Box>
                                        <Box w="auto" h="40px">
                                            <Text as="span" fontWeight={500}>
                                                Código:{' '}
                                            </Text>
                                            {
                                                agents[
                                                    Number(selectAgentLink[0])
                                                ]?.codigo
                                            }
                                        </Box>
                                    </Box>
                                ) : (
                                    <Text color="red.500" textAlign="center">
                                        Agente Financeiro não encontrado,{' '}
                                        <Link
                                            as={LinkRouter}
                                            to="/agentes-financeiros"
                                            textDecoration="underline"
                                            fontWeight={600}
                                        >
                                            cadastre agora
                                        </Link>
                                        .
                                    </Text>
                                )}
                            </Layout>
                        </Layout>
                        <Layout type="row">
                            <Layout type="col" textAlign="end">
                                <Button
                                    colorScheme="primary"
                                    variant="outline"
                                    onClick={() => {
                                        if (currentStep === 1) {
                                            onCancel()
                                        } else {
                                            goToPrevStep()
                                        }
                                    }}
                                >
                                    {currentStep === 1 ? 'Cancelar' : 'Voltar'}
                                </Button>
                            </Layout>
                            <Layout type="col">
                                <Button
                                    isDisabled={
                                        currentStep === 2 &&
                                        selectAgentLink &&
                                        selectAgentLink?.length === 1
                                    }
                                    colorScheme="primary"
                                    type={btnCtaText[currentStep].type}
                                    isLoading={isSubmitting || searching}
                                    onClick={e => {
                                        !agents?.length &&
                                            handleConsultAgent({
                                                ...watch()
                                            })
                                        agents?.length && goToNextStep()

                                        btnCtaText[currentStep].type !==
                                            'submit' && e?.preventDefault()
                                    }}
                                >
                                    {btnCtaText[currentStep].text}
                                </Button>
                            </Layout>
                        </Layout>
                    </Layout>
                </form>
            </ModalBuild>
        </>
    )
}

export default Agent
